import React, { useEffect, useRef } from 'react';
import type { ContextMenuItem } from 'pos-client/components/customScheduler/types/schedulerTypes';

import './contextMenu.css';

/**
 * ContextMenu Component
 *
 * Displays a context menu at a specified position with configurable menu items.
 * Handles click events on menu items and closes when clicking outside.
 *
 * Features:
 * - Positioned at click location
 * - Supports custom menu items with actions
 * - Auto-closes on outside click
 * - Keyboard support (Escape to close)
 */

interface ContextMenuProps {
  visible: boolean;
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

const ContextMenu = React.forwardRef<HTMLDivElement, ContextMenuProps>(
  ({ visible, x, y, items, onClose }, ref) => {
    const menuRef = useRef<HTMLDivElement>(null);

    // Handle outside clicks
    useEffect(() => {
      if (!visible) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [visible, onClose]);

    if (!visible) {
      return null;
    }

    return (
      <div
        ref={ref || menuRef}
        className="context-menu"
        style={{
          position: 'fixed',
          top: `${y}px`,
          left: `${x}px`,
          zIndex: 1000,
        }}
      >
        <ul className="context-menu-list">
          {items.map((item) => (
            <li key={item.id} className="context-menu-item">
              <button
                className="context-menu-button"
                onClick={() => {
                  item.action();
                  onClose();
                }}
              >
                {item.icon && (
                  <span className="context-menu-icon">{item.icon}</span>
                )}
                <span className="context-menu-label">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
);

ContextMenu.displayName = 'ContextMenu';

export default ContextMenu;
